import { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { useEntityPicker } from './useEntityPicker';
import { CreateTableForEmployeeSelect } from "../../../components/ManageTable";
import loadingSpinner from '../../../assets/loading.gif';
import { FailMessage, SearchBar } from '../../APICommon';

/**
 * Generic EntityPickerModal
 * 
 * Reusable modal component for selecting any entity type
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.entityName - Display name (e.g., "Employee", "Vessel")
 * @param {Array} config.tableHeaders - Table column headers
 * @param {Array} config.tableAccessors - Table column accessors (field names)
 * @param {Array} config.searchFields - Search filter configurations
 * @param {string} config.apiEndpoint - API endpoint
 */
export function EntityPickerModal({ isOpen, onClose, setData, config }) {
	const [failMessage, setFailMessage] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [selectedEntity, setSelectedEntity] = useState(null);
	const [filterDataIndex, setFilterDataIndex] = useState(0);
	const [searchPlaceholder, setSearchPlaceholder] = useState("Search...");

	const { entities, loading, fetchData, handleFilter } = useEntityPicker(config);

	const handleClose = () => {
		onClose();
		setFailMessage("");
	};

	const handleProceed = () => {
		if (!selectedEntity) {
			setFailMessage(`Please select a ${config.entityName.toLowerCase()} first.`);
			return;
		}
		onClose();
		setData(selectedEntity);
	};

	// Fetch data when modal opens
	useEffect(() => {
		if (isOpen) {
			setSelectedEntity(null);
			setCurrentPage(1);
			fetchData();
		}
	}, [isOpen]);

	// Update search placeholder based on selected filter
	useEffect(() => {
		const filterConfig = config.searchFields.find(f => f.index === filterDataIndex);
		if (filterConfig) {
			if (filterConfig.field === "all") {
				// Build placeholder from all non-"all" fields
				const fields = config.searchFields
					.filter(f => f.field !== "all")
					.map(f => f.label.toLowerCase())
					.join(" or ");
				setSearchPlaceholder(`Search by ${fields}`);
			} else {
				setSearchPlaceholder(`Search by ${filterConfig.label.toLowerCase()}`);
			}
		}
		if (isOpen) fetchData();
	}, [filterDataIndex]);

	return (
		<>
			<div className={`modal-overlay ${isOpen ? 'open' : ''}`}>
				{isOpen && (
					<div className="modal">
						<div className="popUp-header row">
							<div className="popUp-title">
								<h3 style={{ color: "#668fca" }}>Select {config.entityName}</h3>
							</div>
							<div className="popUp-close">
								<CloseIcon onClick={handleClose} />
							</div>
						</div>

						<div className="transaction-body">
							<EntityPickerFilter
								filterOptions={config.searchFields}
								setFilterDataIndex={setFilterDataIndex}
							/>
							<div className="transaction-body-top row">
								<SearchBar
									placeholder={searchPlaceholder}
									handleFilter={(value) => handleFilter(value, filterDataIndex)}
								/>
							</div>
						</div>

						<div className="popUp-body">
							<div className="select-employee-table">
								{loading ? (
									<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '10vh' }}>
										<img src={loadingSpinner} alt="Loading..." />
									</div>
								) : entities.length > 0 ? (
									<CreateTableForEmployeeSelect
										dataList={entities}
										dataHeader={config.tableHeaders}
										dataAccessor={config.tableAccessors}
										setSelectedEmployee={setSelectedEntity}
										currentPage={currentPage}
										setCurrentPage={setCurrentPage}
									/>
								) : (
									<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '10vh' }}>
										<p style={{ color: '#999', fontSize: '14px' }}>No {config.entityName.toLowerCase()}s found</p>
									</div>
								)}
							</div>
						</div>

						<div className="buttons row" style={{ justifyContent: "end", padding: "50px 20px 0 20px" }}>
							<button
								id={`add-${config.entityName.toLowerCase()}`}
								className="save row"
								onClick={handleProceed}>
								PROCEED
							</button>
						</div>
					</div>
				)}
			</div>

			<FailMessage message={failMessage} onClose={() => setFailMessage("")} />
		</>
	);
}

/**
 * Filter dropdown component
 */
function EntityPickerFilter({ filterOptions, setFilterDataIndex }) {
	const [selectedOption, setSelectedOption] = useState(filterOptions[0].label);

	const handleSelectChange = (event) => {
		setSelectedOption(event.target.value);
		const match = filterOptions.find(o => o.label === event.target.value);
		if (match) setFilterDataIndex(match.index);
	};

	return (
		<div className="filters row" style={{ alignItems: "center" }}>
			<span style={{ marginRight: "10px" }}>Filter</span>
			<select
				className="filterSelect"
				value={selectedOption}
				onChange={handleSelectChange}
			>
				{filterOptions.map((opt) => (
					<option key={opt.label} value={opt.label}>
						{opt.label}
					</option>
				))}
			</select>
		</div>
	);
}